/**
 * PRE-188 — Requirements Traceability Matrix.
 *
 * One searchable, filterable row per documented requirement: flow steps, backlog
 * items and product rules, each carrying its BR ids, code anchors and guarding
 * tests. Rows are derived by `rtmkit` from the document in D1 and served by the
 * API, so this screen is a renderer — nothing is authored here. The document
 * itself is edited on the Business rules and User flow screens.
 *
 * Three layers meet in the table and only one of them is editable:
 *
 *   1. code coverage   — BR anchors + tests, verified by the CI guardrail
 *   2. code verdict    — what the semantic review found the code actually doing
 *   3. product verdict — did this capability actually land? stored in D1
 *
 * Layers 1 and 2 are computed and read-only on purpose: a document that lets an
 * operator edit its own evidence is worthless. Layer 3 is the tick — a step can
 * be implemented and fully tested and still not do the job.
 *
 * PRE-186 removed a fourth lane that read Linear. An issue's status is a fact
 * about a ticket, not about the product, and every finding it produced had to be
 * confirmed against the code anyway — which is what layer 2 now does directly.
 */
import {
  computeCoverage,
  filterRows,
  COVERAGE_VERDICTS,
  type CoverageVerdict,
  type FlowStatus,
  type RtmFilters,
  type RtmKind,
  type RtmViewRow,
} from '@halilural/rtmkit';
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Code,
  Grid,
  Group,
  Loader,
  Modal,
  Progress,
  SegmentedControl,
  Stack,
  Switch,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { IconAlertTriangle, IconSearch } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { useI18n, useT, type MessageKey, type TranslateFn } from '../i18n';
import { adminApi } from '../lib/api';
import { adminKeys } from '../lib/query';

/**
 * PRE-190: status copy used before the document has loaded (the filter chips
 * render immediately). Once the response is in, the document's own labels win —
 * these exist so the controls aren't blank for the first paint, not as a second
 * source of truth.
 *
 * The document's labels come from D1 in whatever language the product owner
 * wrote them, so they are NOT translated here; only this fallback is.
 */
const fallbackStatusLabel = (t: TranslateFn): Record<FlowStatus, string> => ({
  ready: t('rtm.status.ready'),
  todo: t('rtm.status.todo'),
  gap: t('rtm.status.gap'),
});

/** Row-kind labels are panel copy, so they live with the panel. */
const kindLabel = (t: TranslateFn, kind: RtmKind): string => t(`rtm.kind.${kind}` as MessageKey);

/** Verdict copy, likewise: rtmkit ships no user-facing strings. */
const verdictLabel = (t: TranslateFn, verdict: CoverageVerdict): string =>
  t(`rtm.verdict.${verdict}` as MessageKey);

const kindOptions = (t: TranslateFn): { label: string; value: RtmKind | 'all' }[] => [
  { label: t('rtm.all'), value: 'all' },
  { label: kindLabel(t, 'step'), value: 'step' },
  { label: kindLabel(t, 'backlog'), value: 'backlog' },
  { label: kindLabel(t, 'rule'), value: 'rule' },
];

/** Stable identity for the "no rows yet" case (see `allRows` below). */
const EMPTY_ROWS: RtmViewRow[] = [];

const statusOptions = (
  t: TranslateFn,
  label: Record<FlowStatus, string>,
): { label: string; value: FlowStatus | 'all' }[] => [
  { label: t('rtm.all'), value: 'all' },
  { label: label.ready, value: 'ready' },
  { label: label.todo, value: 'todo' },
  { label: label.gap, value: 'gap' },
];

/** Small stat tile for the coverage header. */
function Stat({
  label,
  value,
  hint,
  color,
}: {
  label: string;
  value: string;
  hint: string;
  color?: string;
}) {
  return (
    <Card withBorder padding="md" radius="md" h="100%">
      <Text size="xs" tt="uppercase" fw={700} c="dimmed">
        {label}
      </Text>
      <Text fw={800} size="xl" c={color}>
        {value}
      </Text>
      <Text size="xs" c="dimmed">
        {hint}
      </Text>
    </Card>
  );
}

/**
 * The graded decision: verdict and reason for one requirement.
 *
 * The row's checkbox is the fast path — "I accept this finding" — and it can only
 * ever say `covered`. This is where the other three verdicts live, because
 * `partial` is the document's most valuable finding: built, but not doing the
 * job. A verdict other than `covered` is unusable a month later without a reason,
 * so the reason is mandatory.
 */
function DecisionModal({ row, onClose }: { row: RtmViewRow; onClose: () => void }) {
  const t = useT();
  const queryClient = useQueryClient();
  const [verdict, setVerdict] = useState<CoverageVerdict>(row.decision?.verdict ?? 'covered');
  const [note, setNote] = useState(row.decision?.note ?? '');
  const [error, setError] = useState<string | null>(null);

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['admin', 'rtm'] });
  };

  const save = useMutation({
    mutationFn: () => adminApi.putRtmCoverage(row.id, { verdict, note }),
    onSuccess: async () => {
      await invalidate();
      onClose();
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  const clear = useMutation({
    mutationFn: () => adminApi.clearRtmCoverage(row.id),
    onSuccess: async () => {
      await invalidate();
      onClose();
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  // Mirrors rtmkit's own rule, so the button is disabled rather than the request
  // rejected: anything but "karşılandı" needs a reason a reader can use later.
  const needsNote = verdict !== 'covered' && note.trim().length === 0;

  return (
    <Modal opened onClose={onClose} size="lg" title={t('rtm.decisionTitle', { id: row.id })}>
      <Stack gap="md">
        <div>
          <Text fw={600}>{row.title}</Text>
          <Text size="sm" c="dimmed">
            {row.detail}
          </Text>
        </div>

        <SegmentedControl
          fullWidth
          value={verdict}
          onChange={(value) => {
            setVerdict(value as CoverageVerdict);
          }}
          data={COVERAGE_VERDICTS.map((value) => ({ label: verdictLabel(t, value), value }))}
        />

        <Textarea
          label={t('rtm.reason')}
          description={t('rtm.reasonHint')}
          minRows={3}
          autosize
          value={note}
          onChange={(e) => {
            const value = e.currentTarget.value;
            setNote(value);
          }}
          error={needsNote ? t('rtm.reasonRequired') : null}
        />

        {error ? (
          <Alert color="red" variant="light">
            {error}
          </Alert>
        ) : null}

        <Group justify="space-between">
          <Button
            variant="subtle"
            color="red"
            disabled={!row.decision || clear.isPending}
            onClick={() => {
              clear.mutate();
            }}
          >
            {t('rtm.clearDecision')}
          </Button>
          <Group>
            <Button variant="default" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button
              disabled={needsNote}
              loading={save.isPending}
              onClick={() => {
                setError(null);
                save.mutate();
              }}
            >
              {t('common.save')}
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}

export function TraceabilityScreen() {
  const { t, locale } = useI18n();
  const [filters, setFilters] = useState<RtmFilters>({
    query: '',
    kind: 'all',
    status: 'all',
    untracedOnly: false,
  });
  /**
   * Decided or not — both directions.
   *
   * "Yalnızca karar verilmemişler" answers what is left to do and nothing else.
   * Going back over what you already approved is the other half of reviewing a
   * document, and there was no way to ask for it.
   */
  const [decision, setDecision] = useState<'all' | 'decided' | 'undecided'>('all');
  const [editing, setEditing] = useState<string | null>(null);

  const rtm = useQuery({
    queryKey: adminKeys.rtm(),
    queryFn: () => adminApi.getRtm(),
  });

  // PRE-190: the requirements themselves now come from D1 with the verdicts, so
  // there is no bundled copy to fall back to when the API is unreachable. An
  // empty table plus the error below is the honest outcome — the previous
  // offline projection could quietly show requirements the document no longer had.
  // A fresh `?? []` on every render would invalidate every memo below it, so the
  // empty case gets one stable identity.
  const allRows = useMemo(() => rtm.data?.rows ?? EMPTY_ROWS, [rtm.data]);
  const meta = rtm.data?.meta;
  const statusLabel = rtm.data?.labels.status ?? fallbackStatusLabel(t);
  const statusColor = rtm.data?.labels.statusColor;
  const STATUS_OPTIONS = statusOptions(t, statusLabel);
  const KIND_OPTIONS = kindOptions(t);
  const rows = useMemo(() => {
    const filtered = filterRows(allRows, filters) as RtmViewRow[];
    if (decision === 'decided') return filtered.filter((row) => Boolean(row.decision));
    if (decision === 'undecided') return filtered.filter((row) => !row.decision);
    return filtered;
  }, [allRows, filters, decision]);

  const total = useMemo(() => computeCoverage(allRows), [allRows]);
  const shown = useMemo(() => computeCoverage(rows), [rows]);
  const stats = rtm.data?.stats;
  const editingRow = allRows.find((row) => row.id === editing) ?? null;

  const staleDays = useMemo(() => {
    if (!meta) return 0;
    const verified = Date.parse(meta.verifiedAt);
    return Math.floor((Date.now() - verified) / 86_400_000);
  }, [meta]);
  const isStale = meta ? staleDays > meta.staleAfterDays : false;

  /** Distinct `BR-…` ids the document actually references — the headline that
   *  answers "how much of the rule catalog is this document connected to". */
  const referencedRules = useMemo(() => new Set(allRows.flatMap((row) => row.br)).size, [allRows]);

  return (
    <Stack gap="xl">
      <div>
        <Text c="dimmed" size="sm">
          {t('rtm.intro')}
        </Text>
      </div>

      {rtm.isError ? (
        <Alert color="red" variant="light" icon={<IconAlertTriangle size={18} />}>
          {t('rtm.apiUnreachable')}
        </Alert>
      ) : null}

      {isStale ? (
        <Alert color="orange" variant="light" icon={<IconAlertTriangle size={18} />}>
          {t('rtm.stale', {
            date: meta?.verifiedAt ?? '—',
            days: staleDays,
            threshold: meta?.staleAfterDays ?? 0,
          })}
        </Alert>
      ) : null}

      <Grid gap="sm">
        <Grid.Col span={{ base: 6, md: 3 }}>
          <Stat
            label={t('rtm.traceability')}
            value={new Intl.NumberFormat(locale, { style: 'percent' }).format(
              total.tracedPct / 100,
            )}
            hint={t('rtm.tracedHint', { traced: total.traced, total: total.total })}
            color={total.tracedPct >= 80 ? 'teal' : 'orange'}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 6, md: 3 }}>
          <Stat
            label={t('rtm.decided')}
            value={stats ? `${String(stats.decided)} / ${String(stats.total)}` : '—'}
            hint={
              stats
                ? t('rtm.decidedHint', {
                    covered: stats.byVerdict.covered,
                    partial: stats.byVerdict.partial,
                    notCovered: stats.byVerdict.not_covered,
                  })
                : t('rtm.decidedFallback')
            }
            color={stats && stats.undecided === 0 ? 'teal' : 'orange'}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 6, md: 3 }}>
          <Stat
            label={t('rtm.untraced')}
            value={String(total.untraced)}
            hint={t('rtm.untracedHint')}
            color={total.untraced === 0 ? 'teal' : 'red'}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 6, md: 3 }}>
          <Stat
            label={t('rtm.referencedBr')}
            value={String(referencedRules)}
            hint={t('rtm.referencedBrHint')}
          />
        </Grid.Col>
      </Grid>

      <Card withBorder padding="md" radius="md">
        <Group justify="space-between" mb="xs">
          <Text size="sm" fw={600}>
            {t('rtm.statusSpread')}
          </Text>
          <Group gap="xs">
            <Badge color="teal" variant="light" radius="sm">
              {statusLabel.ready} {total.byStatus.ready}
            </Badge>
            <Badge color="indigo" variant="outline" radius="sm">
              {statusLabel.todo} {total.byStatus.todo}
            </Badge>
            <Badge color="red" variant="light" radius="sm">
              {statusLabel.gap} {total.byStatus.gap}
            </Badge>
          </Group>
        </Group>
        <Progress.Root size="lg" radius="sm">
          <Progress.Section
            value={(total.byStatus.ready / total.total) * 100}
            color="teal"
            aria-label={statusLabel.ready}
          />
          <Progress.Section
            value={(total.byStatus.todo / total.total) * 100}
            color="indigo"
            aria-label={statusLabel.todo}
          />
          <Progress.Section
            value={(total.byStatus.gap / total.total) * 100}
            color="red"
            aria-label={statusLabel.gap}
          />
        </Progress.Root>
      </Card>

      <Stack gap="sm">
        <TextInput
          placeholder={t('rtm.searchPlaceholder')}
          leftSection={<IconSearch size={16} />}
          value={filters.query}
          onChange={(e) => {
            // Event'i updater'ın DIŞINDA oku: React updater'ı sonra (StrictMode'da
            // iki kez) çağırır ve o an `currentTarget` null olur.
            const query = e.currentTarget.value;
            setFilters((f) => ({ ...f, query }));
          }}
        />
        <Group gap="md" wrap="wrap">
          <SegmentedControl
            size="xs"
            data={KIND_OPTIONS}
            value={filters.kind}
            onChange={(value) => {
              setFilters((f) => ({ ...f, kind: value as RtmKind | 'all' }));
            }}
          />
          <SegmentedControl
            size="xs"
            data={STATUS_OPTIONS}
            value={filters.status}
            onChange={(value) => {
              setFilters((f) => ({ ...f, status: value as FlowStatus | 'all' }));
            }}
          />
          <Switch
            size="sm"
            label={t('rtm.untracedOnly')}
            checked={filters.untracedOnly}
            onChange={(e) => {
              const untracedOnly = e.currentTarget.checked;
              setFilters((f) => ({ ...f, untracedOnly }));
            }}
          />
          <SegmentedControl
            size="xs"
            data={[
              { value: 'all', label: t('rtm.allDecisions') },
              { value: 'decided', label: t('rtm.myApprovals') },
              { value: 'undecided', label: t('rtm.awaitingMe') },
            ]}
            value={decision}
            onChange={(value) => {
              setDecision(value as 'all' | 'decided' | 'undecided');
            }}
          />
          {rtm.isFetching ? <Loader size="xs" /> : null}
          <Text size="sm" c="dimmed">
            {t('rtm.rowCount', { shown: shown.total, total: total.total })}
          </Text>
        </Group>
      </Stack>

      <Card withBorder padding={0} radius="md">
        <Table.ScrollContainer minWidth={1350}>
          <Table striped highlightOnHover verticalSpacing="sm" horizontalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={70}>{t('rtm.colId')}</Table.Th>
                <Table.Th w={110}>{t('rtm.colKind')}</Table.Th>
                <Table.Th>{t('rtm.colRequirement')}</Table.Th>
                <Table.Th w={150}>{t('rtm.colBusinessRule')}</Table.Th>
                <Table.Th w={240}>{t('rtm.colCodeTest')}</Table.Th>
                <Table.Th w={100}>{t('common.status')}</Table.Th>
                <Table.Th w={190}>{t('rtm.colDecision')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((row) => (
                <Table.Tr key={row.id}>
                  <Table.Td>
                    <Code>{row.id}</Code>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">
                      {kindLabel(t, row.kind)}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {row.group}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" fw={600}>
                      {row.title}
                    </Text>
                    <Text size="xs" c="dimmed" lineClamp={2}>
                      {row.detail}
                    </Text>
                    {row.links.length > 0 ? (
                      <Text size="xs" c="indigo" mt={4}>
                        → {row.links.join(', ')}
                      </Text>
                    ) : null}
                  </Table.Td>
                  <Table.Td>
                    {row.br.length > 0 ? (
                      <Group gap={4}>
                        {row.br.map((br) => (
                          <Anchor
                            key={br}
                            component={Link}
                            to="/business-rules"
                            size="xs"
                            underline="never"
                          >
                            <Badge variant="light" color="gray" radius="sm" size="sm">
                              {br}
                            </Badge>
                          </Anchor>
                        ))}
                      </Group>
                    ) : (
                      <Badge variant="outline" color="red" radius="sm" size="sm">
                        {t('rtm.noBr')}
                      </Badge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Stack gap={2}>
                      {row.code.map((path) => (
                        <Text key={path} size="xs" ff="monospace" c="dimmed">
                          {path}
                        </Text>
                      ))}
                      {row.tests.map((path) => (
                        <Text key={path} size="xs" ff="monospace" c="teal">
                          ✓ {path}
                        </Text>
                      ))}
                      {row.code.length === 0 && row.tests.length === 0 ? (
                        <Text size="xs" c="dimmed">
                          —
                        </Text>
                      ) : null}
                    </Stack>
                  </Table.Td>
                  <Table.Td>
                    <Badge
                      color={statusColor?.[row.status]}
                      variant={row.status === 'todo' ? 'outline' : 'light'}
                      radius="sm"
                      size="sm"
                    >
                      {statusLabel[row.status]}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    {(() => {
                      // PRE-139: the tick is gone with the lane it agreed with.
                      // It meant "I accept this finding", and a checkbox that
                      // consents to nothing is worse than no checkbox — the
                      // verdict here is now stated outright, by a person, in the
                      // decision dialog.
                      return (
                        <Stack gap={4} align="flex-start">
                          {row.decision ? (
                            <Text size="xs" c="dimmed">
                              {row.decision.decidedBy} · {row.decision.decidedAt.slice(0, 10)}
                            </Text>
                          ) : null}
                          <Button
                            size="compact-xs"
                            variant="subtle"
                            disabled={rtm.isError}
                            onClick={() => {
                              setEditing(row.id);
                            }}
                          >
                            {t('rtm.decide')}
                          </Button>
                        </Stack>
                      );
                    })()}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Card>

      {editingRow ? (
        <DecisionModal
          key={editingRow.id}
          row={editingRow}
          onClose={() => {
            setEditing(null);
          }}
        />
      ) : null}

      <div>
        <Title order={4} mb="xs">
          {t('rtm.howVerified')}
        </Title>
        <Text size="sm" c="dimmed">
          {t('rtm.howVerifiedBody', { catalog: meta?.ruleCatalog ?? '—' })}
        </Text>
      </div>
    </Stack>
  );
}
